class ResourcesArticles {
  constructor(container) {
    this.container = container;
    this.sectionId = container.dataset.ajaxSectionId;
    this.blogUrl = container.dataset.blogUrl;
    this.gridContainer = container.querySelector('[data-resources-results]');
    this.tagButtons = container.querySelectorAll('[data-resources-tag]');
    this.searchInput = container.querySelector('[data-resources-search]');
    this.searchTimer = null;

    this.currentTag = '';
    this.currentPage = 1;
    this.searchQuery = '';
    this.isLoading = false;

    if (!this.sectionId || !this.gridContainer) {
      console.warn('ResourcesArticles: missing section ID or results container.');
      return;
    }

    this.bindEvents();
    this.syncInitialGrid();
  }

  bindEvents() {
    this.tagButtons.forEach((button) => {
      button.addEventListener('click', () => {
        const tag = button.dataset.resourcesTag || '';
        if (tag === this.currentTag) return;

        this.currentTag = tag;
        this.currentPage = 1;
        if (this.searchInput) {
          this.searchInput.value = '';
          this.searchQuery = '';
        }
        this.setActiveTag(button);
        this.loadArticles();
      });
    });

    if (this.searchInput) {
      this.searchInput.addEventListener('input', () => {
        clearTimeout(this.searchTimer);
        this.searchTimer = setTimeout(() => {
          this.searchQuery = this.searchInput.value.trim();
          this.currentPage = 1;
          if (this.searchQuery) {
            this.currentTag = '';
            this.tagButtons.forEach((button) => {
              const isAll = button.dataset.resourcesTag === '';
              button.classList.toggle('is-active', isAll);
              button.setAttribute('aria-pressed', isAll ? 'true' : 'false');
            });
          }
          this.loadArticles();
        }, 350);
      });
    }

    this.container.addEventListener('click', (event) => {
      const pageButton = event.target.closest('[data-page]');
      if (!pageButton || pageButton.disabled) return;

      event.preventDefault();
      const page = parseInt(pageButton.dataset.page, 10);
      if (!page || page === this.currentPage) return;

      this.currentPage = page;
      this.loadArticles(true);
    });
  }

  setActiveTag(activeButton) {
    this.tagButtons.forEach((button) => {
      const isActive = button === activeButton;
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });
  }

  buildFetchUrl() {
    const params = new URLSearchParams();
    params.set('section_id', this.sectionId);

    if (this.searchQuery) {
      params.set('q', this.searchQuery);
      params.set('type', 'article');
      if (this.currentPage > 1) {
        params.set('page', String(this.currentPage));
      }
      return `/search?${params.toString()}`;
    }

    let path = this.blogUrl;
    if (this.currentTag) {
      path = `${this.blogUrl}/tagged/${this.currentTag}`;
    }

    if (this.currentPage > 1) {
      params.set('page', String(this.currentPage));
    }

    return `${path}?${params.toString()}`;
  }

  findSection(doc) {
    const fullId = `shopify-section-${this.sectionId}`;
    let section = doc.getElementById(fullId);
    if (section) return section;

    const shortId = this.sectionId.includes('__')
      ? this.sectionId.split('__').pop()
      : this.sectionId;

    return doc.querySelector(`[id$="__${shortId}"]`) || doc.querySelector(`[id$="${shortId}"]`);
  }

  syncInitialGrid() {
    if (this.searchQuery || this.currentTag || this.currentPage > 1) return;
    this.loadArticles();
  }

  async loadArticles(scrollIntoView = false) {
    if (this.isLoading || !this.sectionId || !this.gridContainer) return;

    this.isLoading = true;
    this.container.classList.add('is-loading');

    try {
      const response = await fetch(this.buildFetchUrl());
      if (!response.ok) throw new Error('Failed to load articles');

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const section = this.findSection(doc);

      if (!section) {
        throw new Error(`Section shopify-section-${this.sectionId} not found in response`);
      }

      const results = section.querySelector('[data-resources-results]');
      if (!results) {
        throw new Error('Results container not found in section response');
      }

      this.gridContainer.innerHTML = results.innerHTML;

      if (scrollIntoView) {
        this.container.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    } catch (error) {
      console.error('ResourcesArticles:', error);
    } finally {
      this.isLoading = false;
      this.container.classList.remove('is-loading');
    }
  }
}

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-resources-articles]').forEach((container) => {
    new ResourcesArticles(container);
  });
});
